import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronDownIcon, MapPinIcon, ListBulletIcon, MapIcon } from '@heroicons/react/24/outline';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';
import EntitySearchForm from '../../components/EntitySearchForm';
import Pagination from '../../components/Pagination';
import AppellationMapViewer from '../../components/appellations/AppellationMapViewer';
import GeoJSONImportModal from '../../components/appellations/GeoJSONImportModal';
import client from '../../lib/feathers';

const DESIGNATION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'ava', label: 'AVA' },
  { value: 'aoc', label: 'AOC' },
  { value: 'aop', label: 'AOP' },
  { value: 'doc', label: 'DOC' },
  { value: 'docg', label: 'DOCG' },
  { value: 'do', label: 'DO' },
  { value: 'doca', label: 'DOCa' },
  { value: 'gi', label: 'GI' },
  { value: 'wo', label: 'WO' },
  { value: 'vqa', label: 'VQA' },
  { value: 'ig', label: 'IG' },
];

const DESIGNATION_COLORS = {
  ava: 'bg-blue-100 text-blue-800',
  aoc: 'bg-purple-100 text-purple-800',
  aop: 'bg-purple-100 text-purple-800',
  doc: 'bg-green-100 text-green-800',
  docg: 'bg-emerald-100 text-emerald-800',
  do: 'bg-yellow-100 text-yellow-800',
  doca: 'bg-yellow-100 text-yellow-800',
  gi: 'bg-orange-100 text-orange-800',
  wo: 'bg-pink-100 text-pink-800',
  vqa: 'bg-red-100 text-red-800',
  ig: 'bg-teal-100 text-teal-800',
};

export default function Appellations() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(query.get('page') || 0);
  const [appellations, setAppellations] = useState([]);
  const [total, setTotal] = useState(0);
  const [keywords, setKeywords] = useState(query.get('keywords') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [designationFilter, setDesignationFilter] = useState(query.get('type') || '');
  const [countryFilter, setCountryFilter] = useState(query.get('country') || '');
  const [expanded, setExpanded] = useState({});
  const [children, setChildren] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [showImportModal, setShowImportModal] = useState(false);

  const getAppellations = async () => {
    try {
      setLoading(true);
      setError(null);
      const service = client.service('admin/appellation');
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: { name: 1 },
        parentId: null,
      };

      if (keywords && keywords.length) {
        q.name = { $iLike: `%${keywords}%` };
        delete q.parentId; // Search across all levels
      }

      if (designationFilter) {
        q.designationType = designationFilter;
      }

      if (countryFilter) {
        q.country = countryFilter;
      }

      const result = await service.find({ query: q });
      setAppellations(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading appellations:', e);
      setError('Failed to load appellations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadChildren = useCallback(async (parentId) => {
    try {
      const service = client.service('admin/appellation');
      const result = await service.find({
        query: {
          parentId,
          $limit: 100,
          $sort: { name: 1 },
        }
      });
      setChildren(prev => ({ ...prev, [parentId]: result.data }));
    } catch (e) {
      console.error('Error loading children:', e);
    }
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] };
      if (next[id] && !children[id]) {
        loadChildren(id);
      }
      return next;
    });
  }, [children, loadChildren]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setViewMode('list');
    getAppellations();
  };

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    let url = `/admin/appellations?page=${p}`;
    if (keywords) url += `&keywords=${keywords}`;
    if (designationFilter) url += `&type=${designationFilter}`;
    if (countryFilter) url += `&country=${countryFilter}`;
    navigate(url);
    setPage(p);
  };

  useEffect(() => {
    getAppellations();
  }, [page, designationFilter, countryFilter]);

  const renderRow = (appellation, depth = 0) => {
    const childCount = parseInt(appellation.childrenCount) || 0;
    const isExpanded = expanded[appellation.id];
    const childList = children[appellation.id] || [];
    const designationColor = DESIGNATION_COLORS[appellation.designationType] || 'bg-gray-100 text-gray-800';

    return (
      <React.Fragment key={appellation.id}>
        <tr className="hover:bg-gray-100 cursor-pointer">
          <td className="px-6 py-4 whitespace-nowrap" style={{ paddingLeft: `${24 + depth * 24}px` }}>
            <div className="flex items-center">
              {childCount > 0 ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleExpand(appellation.id); }}
                  className="mr-2 text-gray-400 hover:text-gray-600"
                >
                  {isExpanded
                    ? <ChevronDownIcon className="h-4 w-4" />
                    : <ChevronRightIcon className="h-4 w-4" />
                  }
                </button>
              ) : (
                <span className="mr-2 w-4" />
              )}
              <Link
                to={`/admin/appellations/${appellation.id}`}
                className="text-sm font-medium text-gray-900 hover:text-primary"
              >
                {appellation.name}
              </Link>
              {childCount > 0 && (
                <span className="ml-1.5 text-xs text-gray-400">({childCount})</span>
              )}
              {appellation.shortName && (
                <span className="ml-2 text-xs text-gray-400">({appellation.shortName})</span>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${designationColor}`}>
              {appellation.designationType}
            </span>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {appellation.country}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {appellation.boundary ? (
              <MapPinIcon className="h-5 w-5 text-green-500" title="Has boundary" />
            ) : (
              <span className="text-gray-300">--</span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {appellation.active ? (
              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
            ) : (
              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Inactive</span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <Link
              to={`/admin/appellations/${appellation.id}`}
              className="text-primary hover:text-red-900"
            >
              View
            </Link>
          </td>
        </tr>
        {isExpanded && childList.map(child => renderRow(child, depth + 1))}
        {isExpanded && childList.length === 0 && children[appellation.id] !== undefined && (
          <tr key={`${appellation.id}-empty`}>
            <td colSpan="6" style={{ paddingLeft: `${48 + depth * 24}px` }} className="px-6 py-2 text-xs text-gray-400 italic">
              No sub-appellations
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <Page title="Appellations">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Appellations' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex gap-2">
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Import GeoJSON
          </button>
          <Link
            to="/admin/appellations/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            New Appellation
          </Link>
        </div>
      </div>

      <div className="mt-6 pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Appellations ({total} Total)
          </h3>
          <select
            value={designationFilter}
            onChange={(e) => { setDesignationFilter(e.target.value); setPage(0); }}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          >
            {DESIGNATION_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Country (e.g. US)"
            value={countryFilter}
            onChange={(e) => { setCountryFilter(e.target.value.toUpperCase().slice(0, 2)); setPage(0); }}
            className="w-24 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          />
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-l-md border ${viewMode === 'list' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              <ListBulletIcon className="h-4 w-4 mr-1" />
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-b border-r ${viewMode === 'map' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              <MapIcon className="h-4 w-4 mr-1" />
              Map
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 sm:mt-0">
          <EntitySearchForm onSearch={onSearch} setKeywords={setKeywords} keywords={keywords} />
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {viewMode === 'map' ? (
        <div className="mt-4 bg-white shadow sm:rounded-lg p-4">
          <AppellationMapViewer height="calc(100vh - 280px)" />
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="flex flex-col mt-4">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Boundary
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appellations.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                          No appellations found. {keywords && 'Try adjusting your search.'}
                        </td>
                      </tr>
                    ) : (
                      appellations.map(a => renderRow(a))
                    )}
                  </tbody>
                </table>
                <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <GeoJSONImportModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            getAppellations();
          }}
        />
      )}
    </Page>
  );
}
